/* istanbul ignore file */

import type { ChangeEvent, FocusEvent } from 'react';
import React from 'react';
import { Feedback } from '@noths/polaris-client-ribbons-design-system';
import Downshift from 'downshift';

import {
  ADDRESS_SEARCH_CONTAINER_TESTID,
  ADDRESS_SEARCH_INPUT_TESTID,
} from 'src/components/molecules/AddressSearch/constants/testids';
import { useAddressSearch } from 'src/components/molecules/AddressSearch/hooks/useAddressSearch';
import type { Address } from 'src/components/molecules/AddressSearch/services/getAddress';
import * as styles from 'src/components/molecules/AddressSearch/styles/styles';
import * as groupStyles from 'src/components/molecules/styles/Group.styles';
import { canShowTypingHint } from './helpers';
import { Results } from './Results';

export interface AddressSearchProps {
  country?: string;
  error?: string | null;
  name?: string;
  onAddressSelected?: (address: Address) => void;
  onBlur?: (event: FocusEvent<HTMLInputElement>) => void;
  onFocus?: (event: FocusEvent<HTMLInputElement>) => void;
  onLookupError?: (query: string) => void;
  onNoResults?: (query: string) => void;
  onQueryChanged?: (event: ChangeEvent<HTMLInputElement>) => void;
}

const LABELS = {
  INPUT_LABEL: 'Address finder',
  INPUT_PLACEHOLDER: 'Start typing your address',
  RETRIEVAL_FAILURE: 'Failed to retrieve address details',
};

export const AddressSearch = ({
  country = '',
  error,
  name = 'address-search',
  onAddressSelected = (_address: Address) => {},
  onBlur = (_event: FocusEvent<HTMLInputElement>) => {},
  onFocus = (_event: FocusEvent<HTMLInputElement>) => {},
  onLookupError = (_query: string) => {},
  onNoResults = (_query: string) => {},
  onQueryChanged = (_event: ChangeEvent<HTMLInputElement>) => {},
  ...props
}: AddressSearchProps) => {
  const {
    containerId,
    handleBlur,
    handleChange,
    handleClearContainer,
    handleFocus,
    handleSelection,
    inputRef,
    query,
    results,
    resultsPaneRef,
    showResults,
  } = useAddressSearch(country, onAddressSelected, onQueryChanged, onNoResults, onLookupError);

  const internalOnFocus = (event: FocusEvent<HTMLInputElement>) => {
    handleFocus();
    onFocus(event);
  };

  const internalOnBlur = (event: FocusEvent<HTMLInputElement>) => {
    handleBlur();
    onBlur(event);
  };

  const inputProps = {
    autoComplete: 'no',
    inputvalue: query,
    label: LABELS.INPUT_LABEL,
    name,
    onFocus: internalOnFocus,
    onBlur: internalOnBlur,
    onChange: handleChange,
    placeholder: LABELS.INPUT_PLACEHOLDER,
    ref: inputRef,
    value: query,
    ...props,
  };

  return (
    <Downshift itemToString={(item) => (item ? item.Text : '')} onChange={handleSelection}>
      {({
        getInputProps,
        getItemProps,
        getLabelProps,
        getMenuProps,
        highlightedIndex,
        selectedItem,
      }) => (
        <div data-testid={ADDRESS_SEARCH_CONTAINER_TESTID}>
          <label {...getLabelProps()}>
            <input
              css={styles.input}
              data-testid={ADDRESS_SEARCH_INPUT_TESTID}
              {...getInputProps(inputProps)}
            />
          </label>
          {showResults && (
            <Results
              containerId={containerId}
              forwardRef={resultsPaneRef}
              onClearContainer={handleClearContainer}
              onSelect={handleSelection}
              results={results}
              showClear={!!containerId}
              showTypingHint={canShowTypingHint(containerId, query)}
              {...{ getItemProps, getMenuProps, highlightedIndex, selectedItem }}
            />
          )}
          {error && (
            <div css={groupStyles.fieldFeedbackContainer} role="alert">
              <Feedback error iconAlign="left" id={`${name}-error`} name={error} />
            </div>
          )}
        </div>
      )}
    </Downshift>
  );
};
